import { useEffect, useRef, useCallback } from 'react';
import { supabase } from '../supabaseClient';

const useSessionTimeout = (timeoutMinutes = 30) => {
  const timeoutRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  // Convert minutes to milliseconds
  const timeoutDuration = timeoutMinutes * 60 * 1000;
  const warningTime = 5 * 60 * 1000; // Show warning 5 minutes before timeout

  // Reset the timeout
  const resetTimeout = useCallback(() => {
    lastActivityRef.current = Date.now();
    
    // Clear existing timeouts
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
    }

    // Set warning timeout (5 minutes before auto-logout)
    warningTimeoutRef.current = setTimeout(() => {
      showTimeoutWarning();
    }, timeoutDuration - warningTime);

    // Set auto-logout timeout
    timeoutRef.current = setTimeout(() => {
      handleAutoLogout();
    }, timeoutDuration);
  }, [timeoutDuration, warningTime]);

  // Show timeout warning
  // Show timeout warning with push notification
const showTimeoutWarning = () => {
  // Send browser push notification
  if ('Notification' in window && Notification.permission === 'granted') {
    new Notification('Flirteen - Session Expiring Soon', {
      body: 'Your session will expire in 5 minutes. Click to stay logged in.',
      icon: '/flirteen-logo.png',
      badge: '/favicon.ico',
      requireInteraction: true,
      actions: [
        { action: 'stay', title: 'Stay Logged In' },
        { action: 'logout', title: 'Logout Now' }
      ]
    }).onclick = () => {
      window.focus(); // Bring window to front
      resetTimeout(); // Stay logged in
    };
  }

  // Also show the existing modal in App.js (if user is on the page)
  if (document.hasFocus()) {
    const userChoice = window.confirm(
      'Your session will expire in 5 minutes due to inactivity. Click OK to stay logged in, or Cancel to logout now.'
    );
    
    if (userChoice) {
      resetTimeout();
    } else {
      handleAutoLogout();
    }
  } else {
    // If user is not on the page, auto-reset to give them time to respond to notification
    setTimeout(() => {
      // Check if they're back, otherwise logout
      if (!document.hasFocus()) {
        handleAutoLogout();
      }
    }, 5 * 60 * 1000); // 5 minutes
  }
};

  // Handle automatic logout with notification
const handleAutoLogout = async () => {
  try {
    // Send logout notification
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification('Flirteen - Logged Out', {
        body: 'You have been automatically logged out due to inactivity.',
        icon: '/flirteen-logo.png',
        badge: '/favicon.ico'
      });
    }

    await supabase.auth.signOut();
    
    // Show logout message only if user is on the page
    if (document.hasFocus()) {
      alert('You have been automatically logged out due to inactivity.');
    }
    
    localStorage.removeItem('lastActivity');
    
  } catch (error) {
    console.error('Error during auto-logout:', error);
  }
};

  // Track user activity
  const handleActivity = useCallback(() => {
    resetTimeout();
    
    // Store last activity time in localStorage for cross-tab sync
    localStorage.setItem('lastActivity', Date.now().toString());
  }, [resetTimeout]);

  // Check for activity in other tabs
  const checkCrossTabActivity = useCallback(() => {
    const storedActivity = localStorage.getItem('lastActivity');
    if (storedActivity) {
      const lastActivity = parseInt(storedActivity);
      const timeSinceActivity = Date.now() - lastActivity;
      
      // If there was recent activity in another tab, reset our timeout
      if (timeSinceActivity < lastActivityRef.current && timeSinceActivity < timeoutDuration) {
        resetTimeout();
      }
    }
  }, [resetTimeout, timeoutDuration]);

  useEffect(() => {
    // Activity events to monitor
    const events = [
      'mousedown',
      'mousemove', 
      'keypress',
      'scroll',
      'touchstart',
      'click'
    ];

    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    // Check for cross-tab activity every 30 seconds
    const crossTabInterval = setInterval(checkCrossTabActivity, 30000);

    // Listen for storage changes (activity in other tabs)
    const handleStorageChange = (e) => {
      if (e.key === 'lastActivity') {
        checkCrossTabActivity();
      }
    };
    window.addEventListener('storage', handleStorageChange);

    // Initialize timeout
    resetTimeout();

    // Cleanup
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
      
      clearInterval(crossTabInterval);
      window.removeEventListener('storage', handleStorageChange);
      
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
      }
    };
  }, [handleActivity, resetTimeout, checkCrossTabActivity]);

  // Manual function to extend session
  const extendSession = useCallback(() => {
    resetTimeout();
  }, [resetTimeout]);

  return {
    extendSession,
    resetTimeout
  };
};

export default useSessionTimeout;